/**
 * Approvals — Queue of artifacts pending human sign-off.
 * Each card shows what the agent produced and the "Why this?" explanation.
 */
import { useState } from "react";
import MarketingShell from "@/components/MarketingShell";
import { CheckCircle2, X, Sparkles, ChevronRight, Eye, Linkedin, Instagram, Mail as MailIcon, FileText } from "lucide-react";
import { toast } from "sonner";

const SEED = [];

export default function Approvals() {
  const [items, setItems] = useState(SEED);
  const [expanded, setExpanded] = useState(null);

  const act = (id, action) => {
    setItems(items.filter((i) => i.id !== id));
    toast.success(action === "approve" ? "Approved & queued" : "Rejected · drafts returned to Content Studio");
  };

  return (
    <MarketingShell title="Approvals" subtitle={`${items.length} artifacts pending sign-off`}>
      <div data-testid="approvals-page">
        {items.length === 0 ? (
          <div className="mkt-card mkt-card-pad" style={{ textAlign: "center", padding: 60 }}>
            <CheckCircle2 style={{ width: 32, height: 32, color: "var(--success)", margin: "0 auto 10px" }} />
            <h3 style={{ fontFamily: "var(--font-head)", color: "var(--navy)", fontWeight: 620 }}>Inbox zero</h3>
            <p style={{ fontSize: 12, color: "var(--text-tertiary)" }}>No artifacts pending review.</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {items.map((it) => {
              const Icon = it.icon;
              const open = expanded === it.id;
              return (
                <div key={it.id} className="mkt-card" data-testid={`approval-${it.id}`}>
                  <div style={{ padding: "16px 20px", display: "flex", alignItems: "flex-start", gap: 14 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: `${it.color}15`, color: it.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Icon style={{ width: 16, height: 16 }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <h4 style={{ fontFamily: "var(--font-head)", fontSize: 14, fontWeight: 600, color: "var(--navy)" }}>{it.title}</h4>
                        <span className={`tag ${it.brand === "passed" ? "tag-green" : "tag-amber"}`}>
                          {it.brand === "passed" ? "Brand-safe" : "Review brand"}
                        </span>
                      </div>
                      <p style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 6, lineHeight: 1.55 }}>{it.summary}</p>
                      <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 8, fontSize: 10.5, color: "var(--text-tertiary)" }}>
                        <span><strong style={{ color: "var(--text-secondary)" }}>Template:</strong> {it.template}</span>
                        <span><strong style={{ color: "var(--text-secondary)" }}>Scheduled:</strong> {it.scheduled}</span>
                        <span><strong style={{ color: "var(--text-secondary)" }}>Run:</strong> {it.agentRunId}</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button onClick={() => setExpanded(open ? null : it.id)} className="btn" style={{ height: 30 }} data-testid={`why-${it.id}`}>
                        <Eye style={{ width: 12, height: 12 }} /> {open ? "Hide" : "Why?"}
                      </button>
                      <button onClick={() => act(it.id, "reject")} className="btn" style={{ height: 30, color: "var(--error)" }} data-testid={`reject-${it.id}`}>
                        <X style={{ width: 12, height: 12 }} /> Reject
                      </button>
                      <button onClick={() => act(it.id, "approve")} className="btn btn-primary" style={{ height: 30 }} data-testid={`approve-${it.id}`}>
                        <CheckCircle2 style={{ width: 12, height: 12 }} /> Approve
                      </button>
                    </div>
                  </div>
                  {open && (
                    <div className="ai-banner" style={{ borderRadius: 0, margin: 0 }}>
                      <div className="ai-pill">
                        <Sparkles style={{ width: 11, height: 11 }} /> {it.agent} reasoning
                      </div>
                      <p style={{ fontSize: 12.5, marginTop: 6 }}>{it.why}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </MarketingShell>
  );
}
